import React, { useState, useEffect, useCallback } from 'react';
import { Camera, X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Wand2, Plus, ArrowRight, ArrowLeft, ShieldCheck, Zap, Lock, ExternalLink } from 'lucide-react';
import { CreativeBrief, CampaignImage, AppStep, GenderPreference } from './types';
import { generateCampaignConcepts, generateTwinImage } from './services/geminiService';

declare global {
  var aistudio: {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  };
}

const REALISM_PROFILES = [
  "Master Likeness (Preserve Face)", 
  "2010 Facebook Upload Quality",
  "Deep Fried Saturation (Max Chaos)",
  "TikTok Beauty Filter (Glitchy)",
  "CCTV Footage (Security Cam)",
  "Commercial High-Gloss", 
  "Raw Cinematic 8K", 
  "Shot on iPhone (Lifestyle Pro)", 
  "Cursed Image (Low Res)", 
  "PS1 Graphics / Low Poly", 
  "Early 2000s Blog Post", 
  "Uncanny Valley", 
  "Hyper-Realistic Surrealism", 
  "VHS Artifacts", 
  "Liminal Space Aesthetic", 
  "Potato Camera (2007)"
];

const LIGHTING_OPTIONS = [
  "Red Laser Eyes (Meme Aura)",
  "Blue Light from 10 Monitors",
  "Microwave Glow", 
  "3AM Gas Station Fluorescent", 
  "Nuclear Blast (Overexposed)", 
  "RGB Gamer Room (Max Brightness)", 
  "Single Flickering Bulb", 
  "Studio Softbox (Product Glow)", 
  "Golden Hour (Commercial Radiance)", 
  "TV Static Aura", 
  "Sunset on Mars", 
  "Police Sirens (Red/Blue Flash)",
  "Flash Photography (Paparazzi Style)"
];

const LENS_OPTIONS = [
  "Door Peephole (Curved)",
  "Gameboy Camera (1998 Pixels)",
  "Extreme Fisheye (The Lens of Truth)", 
  "Macro Zoom (Uncomfortably Close)", 
  "35mm (Commercial Lifestyle)", 
  "85mm (Boutique Portrait Bokeh)", 
  "Disposable Camera (Vintage)", 
  "Nokia 3310 Optics", 
  "Wide Angle Panic", 
  "Surveillance Pin-hole", 
  "Dreamy Blurry Blur", 
  "Glitched Viewport"
];

const BODY_TYPE_OPTIONS = ["As in photo", "GigaChad Physique", "NPC Stance", "Inflatable Suit", "Absurdly Buff", "Spaghetti Arms", "Centaur-ish", "Floating Head Only", "Tiny Body / Giant Head", "Paper Flat"];

const MEME_SCENES = [
  "viral treadmill meme", "The Island from Wii Sports", "Guy Fieri's FlavorTown Kitchen", "A 2009 YouTube Tutorial Screen", "The Backrooms (Infinite Yellow)",
  "Burning House (Disaster Meme)", "Walmart Aisle 4 at 3 AM", "Medieval Oil Painting", "Inside a Giant Toaster",
  "Windows XP Wallpaper (Bliss)", "Minecraft Cave with Herobrine", "A 90s Infomercial Set", "Local News (Viral Moment)",
  "Victorian Era Dinner Party", "The Eric Andre Show Set", "Ikea (Infinite Version)", "Submerged (Fully Clothed)",
  "On the Wing of an Airplane", "Abandoned Shopping Mall", "Giant Ball Pit", "Ancient Roman Senate",
  "The Oval Office (Chaotic)", "A Glitchy Matrix Simulation", "Riding a Giant Capybara", "Deep Space Disco",
  "Luxury Yacht (Sinking)", "Aesthetic Pink Cloud World", "Stock Photo Office", "Inside a Pokéball",
  "The DMV (Hellish Version)", "A Giant Cereal Bowl", "A Pepperoni Pizza Surface", "Speakeasy (Everyone is a Cat)",
  "Gucci Store in the Desert", "The Crusty Crab (Realistic)", "Gmod Map (gm_construct)", "Vaporwave Mall 1988",
  "Elevator with an Elephant", "Burj Khalifa Edge", "Low-Poly PS1 Forest", "Inside a Giant Lava Lamp",
  "Apple Store on Mars", "Medieval Battlefield (Selfie)", "1940s Noir Rainy Street", "Waffle House at 2 AM",
  "Inside a Vending Machine", "Moon with a Beach Umbrella", "Giant Computer Motherboard", "Prehistoric T-Rex Jungle",
  "Inside a Giant Swiss Cheese", "Cotton Candy Cloud City", "Alleyway (Quest Giver NPC)", "1970s Sci-Fi Show Set",
  "A Giant Beehive", "Center of a Black Hole", "Underground Bean Bunker", "A Cloud made of Doges", "The set of a 1950s Cigarette Ad",
  "A Ramen Noodle Hot Tub", "Mars Rover Landing Site", "Inside a Lava Lamp (Swirling)", "The Death Star Canteen"
];

const COMMON_MEME_VIBES = [
  "NPC Energy", "God Tier Shitpost", "Sigma Grindset (Satirical)", "POV: You're at a 2012 House Party",
  "Coquette Aesthetic (Aggressive)", "Goblin Mode", "Main Character Syndrome", "Cursed Energy",
  "Chaos Incarnate", "Confused Screaming", "Early Internet Core", "Surrealist Nightmare",
  "Dank Meme Aesthetic", "Dramatic Renaissance", "Eldritch Horror (Funny)", "Vaporwave Melancholy",
  "Clickbait Thumbnail", "Awkward Family Photo"
];

const FEMALE_OPTIONS = {
  vibe: COMMON_MEME_VIBES,
  scene: MEME_SCENES,
  outfit: [
    "As in photo", "Santa Claus Suit", "Giant Hot Dog Suit", "The 'Had to do it to em' Fit", "Guy Fieri Flame Shirt",
    "Full Camo (Invisible)", "Propeller Hat & Lollipop", "Medieval Suit of Armor", "Clown Couture",
    "Shrek Ear Headband", "Victorian Child Nightgown", "Dinosaur Onesie", "Inflatable T-Rex Suit",
    "Green Screen Morphsuit", "T-Shirt with your own face", "Banana Costume", "Wizard Robes & Pointy Hat",
    "Taco Bell Uniform", "Cyberpunk Trash Bag Poncho", "Bernie Sanders Mittens Fit", "Post-it Note Suit",
    "Astronaut with Cowboy Hat", "Gas Station Ballgown", "Spaghetti Ghillie Suit", "Inflatable Sumo Suit",
    "80s Neon Workout Spandex", "Sliced Bread Robe", "Tuxedo with Scuba Flippers", "Cardboard Box Robot",
    "Bathrobe & Tactical Rifle", "Steampunk Explorer Gear", "Bubble Wrap Dress", "Flamethrower Housewife 50s",
    "Ancient Pharaoh Robes", "Pirate Regalia & Parrot", "Disco Ball Mirror Suit", "Inflatable Shark Suit",
    "High-Fashion Trash Bag", "Giant Chicken Suit", "Cyberpunk Neon Samurai", "Caveman Furs",
    "Bedsheet Toga", "Glitchy 1920s Flapper", "Giant Baby Onesie", "Bee-keeper Suit", "Dollar Bill Suit",
    "18th Century Wig & Gown", "Giant Taco Costume", "Alien Abduction Inflatable", "Heavy Metal Spikes & Leather",
    "Barrel with Suspenders", "Scuba Gear in Library", "90s Windbreaker Chaos", "LED Strip Suit",
    "Medieval Peasant + iPhone", "Giant Thumb Costume", "A Suit Made of Slices of Cheese", "Full Body Bubble Wrap",
    "Subway Employee Uniform", "Midsommar Flower Crown & Robe", "Steam-punk Aviator", "1950s Space Cadet"
  ],
  hair: ["As in photo", "Absolute Bedhead", "Neon Pink Mullet", "Bald Cap", "Einstein Frizz", "Helmet Hair", "Long Textured Mess", "Static Electricity Hair"],
  makeup: ["As in photo", "Full Clown Face", "Extreme Contouring Fail", "Crying Glitter", "Emoji Face Paint", "Sharpie Ey eyebrows", "Ghostly Pale"],
  anatomicalLabels: { makeup: "Environment Chaos", bust: "Torso Logic", waist: "Midsection Warp", hips: "Leg Logic" },
  anatomicalValues: { bust: ["As in photo", "Inflatable", "Hollow", "Abstract Shape"], waist: ["As in photo", "Invisible", "Twisted", "Extra Long"], hips: ["As in photo", "Wheels Instead", "Spider Legs", "Floating"] }
};

const MALE_OPTIONS = {
  vibe: COMMON_MEME_VIBES,
  scene: MEME_SCENES,
  outfit: [
    "As in photo", "Santa Claus Suit", "The 'Had to do it to em' Fit", "Guy Fieri Flame Shirt", "Full Camo (Invisible)",
    "Spider-Man Pointing Suit", "Blue Shirt Guy (NPC Fit)", "Propeller Hat & Lollipop", "Full Armor (Rusty)",
    "Giant Hot Dog Suit", "Banana Costume", "T-Shirt with your own face", "Clown Suit",
    "Medieval Peasant Rags", "CEO Suit (5 sizes too big)", "Cardboard Box Robot", "Scuba Gear (No Water)",
    "Dinosaur Onesie", "Wizard Robes", "Fast Food Uniform", "Bathrobe and Crocs",
    "GigaChad Minimalist Fit", "Bernie Sanders Mittens Fit", "Inflatable T-Rex Suit", "Post-it Note Suit",
    "Astronaut with Cowboy Hat", "Spaghetti Ghillie Suit", "Inflatable Sumo Suit", "80s Neon Workout Spandex",
    "Sliced Bread Robe", "Tuxedo with Scuba Flippers", "Steampunk Explorer Gear", "Bubble Wrap Suit",
    "Ancient Pharaoh Robes", "Pirate Regalia & Parrot", "Disco Ball Mirror Suit", "Inflatable Shark Suit",
    "Trash Bag Fashion", "Giant Chicken Suit", "Cyberpunk Neon Samurai", "Caveman Furs",
    "Bedsheet Toga", "Glitchy 1920s Suit", "Giant Baby Onesie", "Bee-keeper Suit", "Dollar Bill Suit",
    "18th Century Wig & Suit", "Giant Taco Costume", "Alien Abduction Inflatable", "Heavy Metal Spikes & Leather",
    "Barrel with Suspenders", "Scuba Gear in Library", "90s Windbreaker Chaos", "LED Strip Suit",
    "Medieval Peasant + iPhone", "Giant Thumb Costume", "A Suit Made of Marshmallows", "Lumberjack with a Tutu",
    "Subway Employee Uniform", "A Suit Made of Credit Cards", "Full Body Armor (Plastic)", "1970s Leisure Suit (Neon)"
  ],
  hair: ["As in photo", "The Mullet (Majestic)", "Bald with a Fringe", "Extreme Static", "Mohawk (Neon)", "Bedhead (Maximum)", "Bowl Cut", "High-Def Texture"],
  makeup: ["As in photo", "Clown Paint", "Heavy Stubble (Glitter)", "Face Tattoos (Bad)", "Mustache (Giant)", "Invisible Face"],
  anatomicalLabels: { makeup: "Grooming Chaos", bust: "Torso Logic", waist: "Core Distortion", hips: "Leg Energy" },
  anatomicalValues: { bust: ["As in photo", "Absurdly Buff", "Noodle Arms", "Invisible"], waist: ["As in photo", "Snatched (Meme)", "Infinite", "Twisted"], hips: ["As in photo", "Centaur Legs", "Floating", "Tiny Legs"] }
};

const OPTIONS_MAP = { female: FEMALE_OPTIONS, male: MALE_OPTIONS };

const PhaseHeader: React.FC<{ phase: string; title: string }> = ({ phase, title }) => (
  <div className="flex flex-col items-center gap-2 mb-10 text-center animate-fade-in">
    <span className="text-[8px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase {phase}</span>
    <h2 className="text-xl md:text-2xl font-serif text-white tracking-tight headline-style">{title}</h2>
    <div className="w-6 h-[1px] bg-[#dffe00]/30 mt-3" />
  </div>
);

const StudioDropdown: React.FC<{ label: string; value: string; options: any[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[8px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-3.5 px-5 text-[11px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.06] transition-all cursor-pointer hover:border-white/20"
      >
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          return <option key={val} value={val} className="bg-[#050505]">{lbl}</option>;
        })}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const AnchorSlot: React.FC<{ label: string; value: string | null; onUpload: (data: string | null) => void }> = ({ label, value, onUpload }) => (
  <div className="flex flex-col gap-3 flex-1">
    <label className="text-[8px] font-bold uppercase tracking-widest text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer">
          <Plus className="w-6 h-6 stroke-[1px]" />
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (ev) => onUpload(ev.target?.result as string);
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

type StudioTab = 'identity' | 'creative' | 'likeness' | 'optics';

const App: React.FC = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasKey, setHasKey] = useState(false);
  const [customKey, setCustomKey] = useState('');
  const [tempKeyInput, setTempKeyInput] = useState('');
  const [step, setStep] = useState<AppStep>('landing');
  const [activeTab, setActiveTab] = useState<StudioTab>('identity');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [brief, setBrief] = useState<CreativeBrief>({
    gender: 'female', 
    vibe: COMMON_MEME_VIBES[0], 
    scene: "viral treadmill meme", 
    outfit: FEMALE_OPTIONS.outfit[0],
    hairStyle: FEMALE_OPTIONS.hair[0], 
    makeupPalette: FEMALE_OPTIONS.makeup[0], 
    skinStyle: "High Fidelity",
    realismTuning: "Master Likeness (Preserve Face)", 
    bodyType: "As in photo", 
    bustLine: "As in photo", 
    waistRatio: "As in photo", 
    hipsSculpt: "As in photo",
    cameraAngle: "Awkward Eye Level", 
    lighting: LIGHTING_OPTIONS[0], 
    lens: LENS_OPTIONS[0],
    aspectRatio: "9:16", 
    numImages: 4, 
    customPrompt: "", 
    anchorImages: { frontal: null, left: null, right: null }, 
    imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  const checkKey = useCallback(async () => {
    // Priority 1: Check environment variable (Standard injection)
    if (process.env.API_KEY && process.env.API_KEY.length > 5) {
      return true;
    }
    
    // Priority 2: Check AI Studio integration
    if (typeof window.aistudio !== 'undefined' && window.aistudio.hasSelectedApiKey) {
      try {
        const val = await window.aistudio.hasSelectedApiKey();
        return val;
      } catch (e) {
        console.warn("AI Studio check failed", e);
      }
    }
    return false;
  }, []);

  useEffect(() => {
    checkKey()
      .then(val => {
        setHasKey(val);
      })
      .catch(err => {
        console.error("Initialization check failed:", err);
        setHasKey(false);
      })
      .finally(() => {
        setIsInitializing(false);
      });
  }, [checkKey]);

  // Handler for activating via Manual Input
  const handleManualActivate = () => {
    if (tempKeyInput.trim().length < 10) {
      setConnectionError("Invalid API Key format");
      return;
    }
    setCustomKey(tempKeyInput.trim());
    setHasKey(true);
    setConnectionError(null);
  };

  const handleResetKey = () => {
    setHasKey(false);
    setCustomKey('');
    setTempKeyInput('');
    setConnectionError(null);
  };

  const currentOptions = OPTIONS_MAP[brief.gender];

  const switchGender = (g: GenderPreference) => {
    const nextOptions = OPTIONS_MAP[g];
    setBrief(p => ({
      ...p, gender: g, vibe: nextOptions.vibe[0], scene: nextOptions.scene[0], outfit: nextOptions.outfit[0],
      hairStyle: nextOptions.hair[0], makeupPalette: nextOptions.makeup[0], bustLine: "As in photo",
      waistRatio: "As in photo", hipsSculpt: "As in photo"
    }));
  };

  const handleDownloadAll = () => {
    const completedImages = images.filter(img => img.status === 'completed' && img.imageUrl);
    completedImages.forEach((img, index) => {
      const link = document.createElement('a');
      link.href = img.imageUrl!;
      link.download = `meme-twin-asset-${index + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const initiateProduction = async () => {
    if (!brief.anchorImages.frontal) return alert("Please upload at least a Frontal Identity Anchor.");
    setIsGenerating(true);
    try {
      const activeKey = customKey || undefined;
      const generated = await generateCampaignConcepts(brief, activeKey);
      const initialImages: CampaignImage[] = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, themeIndex: generated[0].themeIndex, status: 'idle'
      }));
      setImages(initialImages);
      
      setTimeout(() => {
        document.getElementById('production-results')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);

      for (const img of initialImages) {
        setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'generating' } : p));
        try {
          const url = await generateTwinImage(brief, generated[0].prompt, activeKey);
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'completed', imageUrl: url } : p));
        } catch (e: any) {
          if (e.message === "KEY_RESET_REQUIRED") {
            setHasKey(false);
            setCustomKey('');
            break;
          }
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'failed' } : p));
        }
      }
    } catch (e: any) {
      if (e.message === "KEY_RESET_REQUIRED") {
        setHasKey(false);
        setCustomKey('');
        setConnectionError("The provided key is invalid or has expired.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-8 text-white">
        <div className="flex flex-col items-center gap-6 animate-pulse">
           <div className="w-10 h-10 border-t-2 border-[#dffe00] rounded-full animate-spin" />
           <span className="text-[10px] uppercase tracking-[0.4em] text-[#dffe00]/50 font-serif">Calibrating Optics...</span>
        </div>
      </div>
    );
  }

  if (!hasKey) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 text-white overflow-hidden relative font-sans">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#dffe00]/5 rounded-full blur-[150px] pointer-events-none" />
        
        <div className="max-w-xl w-full relative z-10 flex flex-col items-center text-center">
            {/* Lock Icon - Refined to match 'Hyper Realism' squircle glass look */}
            <div className="w-24 h-24 bg-white/[0.02] border border-white/10 rounded-[2rem] flex items-center justify-center mb-10 backdrop-blur-md shadow-2xl">
              <Lock className="w-8 h-8 text-white/40 stroke-[1.5px]" />
            </div>
            
            {/* Title - Larger, Serif */}
            <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight mb-6">
              AI Twin Studio: <span className="text-[#dffe00]">Meme</span>
            </h1>
            
            {/* Subtitle - Spaced out, small */}
            <p className="text-white/30 text-[10px] uppercase tracking-[0.3em] mb-14 font-medium">
              Connect Studio Access Key to Begin
            </p>
            
            <div className="w-full max-w-sm space-y-4">
               {/* Input - Dark, centered */}
               <div className="relative group">
                 <input 
                   type="password" 
                   value={tempKeyInput}
                   onChange={(e) => {
                     setTempKeyInput(e.target.value);
                     if (connectionError) setConnectionError(null);
                   }}
                   placeholder="Paste Gemini API Key"
                   className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-6 py-4 text-center text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-all text-[13px] tracking-wider"
                 />
               </div>

               {/* Button - White, clean */}
               <button 
                onClick={handleManualActivate}
                className="w-full py-4 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-[#dffe00] transition-colors duration-500 shadow-[0_0_40px_rgba(0,0,0,0.5)]"
               >
                 Activate Studio
               </button>

               {connectionError && (
                 <p className="text-red-500 text-[10px] uppercase tracking-widest pt-2 animate-fade-in">{connectionError}</p>
               )}
            </div>

            {/* Footer - With separator */}
            <div className="mt-16 w-full max-w-xs border-t border-white/5 pt-8 flex justify-center">
              <a 
                href="https://aistudio.google.com/app/api-keys" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 text-white/20 hover:text-white transition-colors text-[9px] uppercase tracking-[0.2em] group"
              >
                Get your free API key from Google <ExternalLink className="w-3 h-3 group-hover:text-[#dffe00] transition-colors" />
              </a>
            </div>
        </div>
      </div>
    );
  }

  if (step === 'landing') {
    return (
      <div className="relative min-h-screen bg-[#050505] flex flex-col items-center justify-center p-8">
        <div className="relative z-10 text-center animate-fade-in max-w-2xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-8">
            <Sparkles className="w-3.5 h-3.5 text-[#dffe00]" />
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#dffe00]/80">Viral Fabricator v1.0</span>
          </div>
          <h1 className="text-4xl md:text-7xl font-serif text-white mb-6 headline-style tracking-[2px]">AI Twin Studio: <span className="text-[#dffe00]">Meme</span></h1>
          <p className="text-white/40 uppercase tracking-[0.4em] text-[10px] mb-12 max-w-sm mx-auto leading-loose">Digitally clone your likeness into the most absurd corners of the internet</p>
          <button 
            onClick={() => setStep('setup')} 
            className="group relative px-14 py-6 border border-white/10 rounded-full text-[11px] font-bold uppercase tracking-[0.4em] hover:bg-white hover:text-black transition-all duration-700 overflow-hidden"
          >
            <span className="relative z-10">Initialize Fabrication</span>
            <div className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-700" />
          </button>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#dffe00]/5 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="absolute bottom-8 left-0 w-full flex justify-center z-20">
          <button 
            onClick={handleResetKey}
            className="text-[9px] uppercase tracking-[0.2em] text-white/20 hover:text-[#dffe00] transition-colors"
          >
            Update API Key
          </button>
        </div>
      </div>
    );
  }

  const tabs: {id: StudioTab, label: string}[] = [
    {id: 'identity', label: 'Identity'},
    {id: 'creative', label: 'Meme Brief'},
    {id: 'likeness', label: 'Likeness + Chaos'},
    {id: 'optics', label: 'Final Polish'}
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#dffe00] selection:text-black pb-32">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-8 flex justify-between items-center bg-gradient-to-b from-[#050505] to-transparent backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setStep('landing')}>
          <div className="w-8 h-8 bg-[#dffe00] rounded-xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-500 shadow-[0_0_20px_rgba(223,254,0,0.2)]">
             <Camera className="w-4 h-4 text-black" />
          </div>
          <span className="font-serif text-[12px] tracking-widest headline-style text-white">AI Twin Studio: <span className="text-[#dffe00]">Meme</span></span>
        </div>
        <button className="text-[9px] font-bold uppercase tracking-[0.4em] text-white/30 hover:text-white transition-colors" onClick={() => setStep('landing')}>Terminate Session</button>
      </nav>

      <main className="max-w-4xl mx-auto px-6 pt-40">
        <div className="flex justify-center mb-16 overflow-x-auto scrollbar-hide">
          <div className="flex bg-white/[0.03] p-2 rounded-full border border-white/10 backdrop-blur-md">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-8 py-3 rounded-full text-[9px] font-bold uppercase tracking-widest whitespace-nowrap transition-all duration-500 ${activeTab === tab.id ? 'bg-[#dffe00] text-black shadow-[0_5px_15px_rgba(223,254,0,0.3)]' : 'text-white/30 hover:text-white'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[400px]">
          {activeTab === 'identity' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="01" title="Identity Anchors" />
              <div className="mb-12 flex bg-white/[0.03] p-1 rounded-full border border-white/5">
                {['female', 'male'].map((g) => (
                  <button key={g} onClick={() => switchGender(g as GenderPreference)} className={`px-10 py-4 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all duration-500 ${brief.gender === g ? 'bg-[#dffe00] text-black' : 'text-white/30 hover:text-white'}`}>
                    {g === 'female' ? 'Female Subject' : 'Male Subject'}
                  </button>
                ))}
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8">
                <AnchorSlot label="Frontal Portrait" value={brief.anchorImages.frontal} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, frontal: val } }))} />
                <AnchorSlot label="Profile (Left)" value={brief.anchorImages.left} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, left: val } }))} />
                <AnchorSlot label="Profile (Right)" value={brief.anchorImages.right} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, right: val } }))} />
              </div>
              <div className="mt-20 w-full flex justify-end">
                <button onClick={() => setActiveTab('creative')} className="group flex items-center gap-3 px-10 py-5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all duration-700">
                  Step 2: Creative <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'creative' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="02" title="Creative Direction" />
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8">
                <StudioDropdown label="CHAOTIC VIBE" value={brief.vibe} options={currentOptions.vibe} onChange={v => setBrief(p => ({ ...p, vibe: v }))} />
                <StudioDropdown label="ABSURD LOCATION" value={brief.scene} options={currentOptions.scene} onChange={v => setBrief(p => ({ ...p, scene: v }))} />
                <StudioDropdown label="RIDICULOUS ATTIRE" value={brief.outfit} options={currentOptions.outfit} onChange={v => setBrief(p => ({ ...p, outfit: v }))} />
              </div>
              <div className="mt-24 w-full flex justify-between items-center">
                <button onClick={() => setActiveTab('identity')} className="flex items-center gap-3 px-8 py-4 text-white/30 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-all">
                  <ArrowLeft className="w-4 h-4" /> Identity
                </button>
                <button onClick={() => setActiveTab('likeness')} className="group flex items-center gap-3 px-10 py-5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all duration-700">
                  Step 3: Likeness <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'likeness' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="03" title="Anatomical Integrity" />
              <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12">
                <StudioDropdown label="Hair Simulation" value={brief.hairStyle} options={currentOptions.hair} onChange={v => setBrief(p => ({ ...p, hairStyle: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.makeup} value={brief.makeupPalette} options={currentOptions.makeup} onChange={v => setBrief(p => ({ ...p, makeupPalette: v }))} />
                <StudioDropdown label="Skin Texture" value={brief.skinStyle} options={["High Fidelity", "Sweaty Glow", "TV Static Aura", "Deep Fried Skin", "Glitched Pixels"]} onChange={v => setBrief(p => ({ ...p, skinStyle: v }))} />
                <StudioDropdown label="Face Preservation" value={brief.realismTuning} options={REALISM_PROFILES} onChange={v => setBrief(p => ({ ...p, realismTuning: v }))} />
                <StudioDropdown label="Body Architecture" value={brief.bodyType} options={BODY_TYPE_OPTIONS} onChange={v => setBrief(p => ({ ...p, bodyType: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.bust} value={brief.bustLine} options={currentOptions.anatomicalValues.bust} onChange={v => setBrief(p => ({ ...p, bustLine: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.waist} value={brief.waistRatio} options={currentOptions.anatomicalValues.waist} onChange={v => setBrief(p => ({ ...p, waistRatio: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.hips} value={brief.hipsSculpt} options={currentOptions.anatomicalValues.hips} onChange={v => setBrief(p => ({ ...p, hipsSculpt: v }))} />
              </div>
              <div className="mt-24 w-full flex justify-between items-center">
                <button onClick={() => setActiveTab('creative')} className="flex items-center gap-3 px-8 py-4 text-white/30 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-all">
                  <ArrowLeft className="w-4 h-4" /> Creative
                </button>
                <button onClick={() => setActiveTab('optics')} className="group flex items-center gap-3 px-10 py-5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all duration-700">
                  Step 4: Optics <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'optics' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="04" title="Final Production" />
              <div className="w-full grid grid-cols-1 md:grid-cols-5 gap-6 mb-16">
                <StudioDropdown label="Atmosphere" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({ ...p, lighting: v }))} />
                <StudioDropdown label="Optics/Glass" value={brief.lens} options={LENS_OPTIONS} onChange={v => setBrief(p => ({ ...p, lens: v }))} />
                <StudioDropdown label="Perspective" value={brief.cameraAngle} options={["Extreme Low Angle", "Uncomfortably Close", "Dutch Tilt (Panic)", "CCTV High Angle"]} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
                <StudioDropdown 
                  label="Framing" 
                  value={brief.aspectRatio} 
                  options={[
                    {value:"9:16", label:"Vertical (9:16)"}, 
                    {value:"1:1", label:"Social (1:1)"}, 
                    {value:"16:9", label:"Wide (16:9)"},
                    {value:"3:4", label:"Classic (3:4)"}
                  ]} 
                  onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} 
                />
                <StudioDropdown 
                  label="Density" 
                  value={brief.imageSize} 
                  options={[
                    {value:"1K", label:"Standard (1K)"}, 
                    {value:"2K", label:"High (2K)"}, 
                    {value:"4K", label:"Ultra (4K)"}
                  ]} 
                  onChange={v => setBrief(p => ({ ...p, imageSize: v as any }))} 
                />
              </div>

              <div className="mt-16 w-full flex flex-col items-center gap-8">
                 <button 
                  onClick={initiateProduction} 
                  disabled={isGenerating} 
                  className="group relative px-20 py-8 bg-[#dffe00] text-black text-[12px] font-bold uppercase tracking-[0.5em] rounded-full hover:bg-white hover:scale-105 active:scale-95 transition-all duration-700 disabled:opacity-30 shadow-[0_25px_60px_rgba(223,254,0,0.3)] disabled:hover:scale-100"
                 >
                   <span className="relative z-10 flex items-center gap-4">
                     {isGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
                     {isGenerating ? "Synthesizing Chaos..." : "Fabricate Meme"}
                   </span>
                 </button>
                 <div className="flex flex-col items-center gap-2">
                   <p className="text-[9px] uppercase tracking-[0.4em] text-white/20">Identity rendering may take 20-40 seconds</p>
                 </div>
              </div>
            </section>
          )}
        </div>

        {images.length > 0 && (
          <section id="production-results" className="flex flex-col items-center pt-32 animate-fade-in border-t border-white/5 mt-24">
             <div className="w-full flex flex-col md:flex-row md:justify-between items-center gap-10 mb-20">
                <div className="space-y-3 text-center md:text-left">
                  <h2 className="text-4xl font-serif text-white headline-style">Viral Assets</h2>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">Fabrication Sequence Complete.</p>
                </div>
                {images.some(img => img.status === 'completed') && (
                  <button 
                    onClick={handleDownloadAll}
                    className="flex items-center gap-4 px-10 py-5 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-[#dffe00] hover:text-black transition-all group shadow-2xl"
                  >
                    <Download className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
                    Batch Export
                  </button>
                )}
             </div>
             <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                {images.map(img => (
                  <div key={img.id} className="aspect-[3/4] bg-white/[0.02] border border-white/5 rounded-[40px] overflow-hidden group relative flex items-center justify-center transition-all duration-700 hover:border-white/20 hover:shadow-2xl">
                    {img.status === 'completed' ? (
                      <>
                        <img src={img.imageUrl} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="Meme" />
                        <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-all duration-700 flex items-center justify-center gap-6 backdrop-blur-xl">
                          <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-5 bg-white text-black rounded-full hover:scale-110 active:scale-90 transition-all shadow-xl"><Maximize2 className="w-5 h-5" /></button>
                          <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'meme.png'; link.click(); }} className="p-5 bg-[#dffe00] text-black rounded-full hover:scale-110 active:scale-90 transition-all shadow-xl"><Download className="w-5 h-5" /></button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-6 p-10 text-center">
                         {img.status === 'failed' ? (
                           <AlertCircle className="w-8 h-8 text-red-500/50" />
                         ) : (
                           <div className="relative">
                              <Loader2 className="w-12 h-12 text-[#dffe00]/20 animate-spin" />
                              <Sparkles className="absolute inset-0 m-auto w-4 h-4 text-[#dffe00] animate-pulse" />
                           </div>
                         )}
                         <div className="space-y-2">
                           <span className="block text-[9px] uppercase tracking-[0.3em] text-white/40 font-bold">
                             {img.status === 'generating' ? 'Synthesizing Chaos' : img.status === 'failed' ? 'Fabrication Failed' : 'In Production Queue'}
                           </span>
                         </div>
                      </div>
                    )}
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>

      <footer className="w-full py-6 text-center mt-12 border-t border-white/5">
        <button 
          onClick={handleResetKey}
          className="text-[9px] uppercase tracking-[0.2em] text-white/20 hover:text-[#dffe00] transition-colors"
        >
          Update API Key
        </button>
      </footer>

      {/* Fullscreen Preview */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/98 flex items-center justify-center p-8 md:p-12 backdrop-blur-3xl animate-fade-in" 
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-6xl w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
             <img src={previewImage} className="max-w-full max-h-full object-contain rounded-3xl shadow-[0_0_100px_rgba(0,0,0,0.5)] border border-white/5" alt="Preview" />
             <button 
              onClick={() => setPreviewImage(null)} 
              className="absolute top-0 right-0 p-5 bg-white/10 hover:bg-red-500 rounded-full transition-all text-white -translate-y-1/2 translate-x-1/2 shadow-2xl"
             >
               <X className="w-6 h-6" />
             </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;